<template>
  <div v-cloak class="grid-container text-md pb-3">
    <pdl-loading page :is-loading="loading" />
    <div v-if="viewOnly && !loading">
      <warranty-claim-view
        :base-properties="baseProperties"
        :claim-data="claimData"
        :display-warranty-make-the-call="displayWarrantyMakeTheCall"
      />
    </div>
    <div v-else>
      <form id="warranty-claim-form" @submit.prevent>
        <warranty-claim-header :base-properties="baseProperties" :claim-data="claimData" :claim-number="claimNumber" />
        <warranty-claim-details
          :base-properties="baseProperties"
          :claim-data="claimData"
          :display-warranty-make-the-call="displayWarrantyMakeTheCall"
        />

        <!-- Claim Lines -->
        <section class="my-6">
          <section-header size="lg" variants="dividing">
            <heading :level="2" qaid="warranty-claim-lines" size="lg" v-html="bp('claimLines')" />
          </section-header>

          <pdl-collapse
            icon-active="indeterminate_check_box"
            icon-inactive="add_box"
            :default-active="false"
            :value="expandedClaims"
            qaid="warranty-claim-collapse"
            @change="claimLineExpansionChange"
          >
            <pdl-collapse-item
              v-for="(claimLine, claimIndex) in claimData.claimLines"
              :key="claimIndex"
              :name="`line${claimIndex}`"
            >
              <div
                slot="title"
                class="heading heading--md"
                qaid="warranty-claim-line-title"
                v-html="bp('claimLineNumber', claimIndex + 1)"
              />

              <warranty-claim-line
                :base-properties="baseProperties"
                :claim-data="claimData"
                :claim-index="claimIndex"
                :claim-line="claimLine"
                :display-warranty-shipment-options="displayWarrantyShipmentOptions"
                :display-warranty-make-the-call="displayWarrantyMakeTheCall"
                @open-delete-dialog="openclaimLineDeleteDialog"
                @save-claim="saveClaim({})"
              />
            </pdl-collapse-item>
          </pdl-collapse>

          <trek-button text underline class="mt-4" qaid="warranty-claim-line-add-claim" @click="addNewClaimLine">
            <span v-html="bp('addClaimLine')"></span>
          </trek-button>

          <hr class="rule my-4" />

          <div class="buttons">
            <trek-button :disabled="claimSubmitted" primary qaid="warranty-claim-submit" @click="submitClaim">
              <span v-html="bp('submitClaim')"></span>
            </trek-button>
            <trek-button secondary qaid="warranty-claim-close" @click="saveClaim">
              <span v-html="bp('saveClose')"></span>
            </trek-button>
            <trek-button
              v-show="claimData.id && claimData.status === 'enumClaimStatusSaved'"
              tertiary
              qaid="warranty-claim-delete"
              @click="isDeleteClaimDialogVisible = true"
            >
              <span v-html="bp('deleteClaim')"></span>
            </trek-button>
          </div>

          <el-dialog
            :visible.sync="isDeleteClaimLineDialogVisible"
            :title="bp('deleteClaimLineConfirm')"
            :center="true"
            :close-on-press-escape="false"
            :close-on-click-modal="false"
            :z-index="16000011"
            :close-dialog-tooltip="bp('closeDialogToolTip')"
            @close="isDeleteClaimLineDialogVisible = false"
          >
            <template slot="footer">
              <div class="buttons buttons--center">
                <trek-button alert qaid="warranty-claim-confirm-delete" @click="deleteClaimLine"
                  ><span v-html="bp('deleteClaimLineYes')"></span>
                </trek-button>
                <trek-button
                  secondary
                  qaid="warranty-claim-cancel-delete"
                  @click="isDeleteClaimLineDialogVisible = false"
                  ><span v-html="bp('cancel')"></span>
                </trek-button>
              </div>
            </template>
          </el-dialog>

          <el-dialog
            :visible.sync="isDeleteClaimDialogVisible"
            :title="getBaseProperty('deleteConfirmCheck')"
            :center="true"
            :close-on-press-escape="false"
            :close-on-click-modal="false"
            :z-index="16000011"
            :close-dialog-tooltip="bp('closeDialogToolTip')"
            @close="isDeleteClaimDialogVisible = false"
          >
            <div v-html="getBaseProperty('claimNotSubmitted')" />

            <template slot="footer">
              <div class="buttons buttons--center">
                <trek-button alert qaid="warranty-claim-confirm-delete" @click="deleteClaim">
                  <span v-html="bp('deleteConfirm')"></span>
                </trek-button>
                <trek-button secondary qaid="warranty-claim-cancel-delete" @click="isDeleteClaimDialogVisible = false">
                  <span v-html="bp('cancel')"></span>
                </trek-button>
              </div>
            </template>
          </el-dialog>
        </section>
      </form>
    </div>
  </div>
</template>

<script>
import {PdlLoading} from '@pedal/pdl-loading';
import SectionHeader from '@/components/SectionHeader';
import axios from 'axios';
import {mapActions, mapMutations, mapState} from 'vuex';
import ElDialog from '@/components/Dialog';
import get from 'lodash/get';
import remove from 'lodash/remove';
import TrekButton from '@/components/TrekButton';
import WarrantyClaimHeader from '@/components/containers/warranty-claim/WarrantyClaimHeader';
import WarrantyClaimDetails from '@/components/containers/warranty-claim/WarrantyClaimDetails';
import {PdlCollapse, PdlCollapseItem} from '@pedal/pdl-collapse';
import Heading from '@/components/Heading';
import every from 'lodash/every';

export default {
  components: {
    PdlCollapse,
    PdlCollapseItem,
    Heading,
    ElDialog,
    TrekButton,
    PdlLoading,
    SectionHeader,
    WarrantyClaimHeader,
    WarrantyClaimDetails,
  },

  props: {
    baseProperties: {
      type: Array,
      required: true,
    },
    claimId: {
      type: String,
      required: true,
    },
    csrf: {
      type: String,
      required: true,
    },
    viewOnly: {
      type: Boolean,
      defaut: false,
      required: false,
    },
    displayWarrantyShipmentOptions: {
      type: Boolean,
      default: true,
    },
    displayWarrantyMakeTheCall: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isDeleteClaimLineDialogVisible: false,
      isDeleteClaimDialogVisible: false,
      claimLineToDelete: null,
      expandedClaims: ['line0'],
      claimNumber: '',
      claimSubmitted: false,
    };
  },

  computed: {
    ...mapState('warrantyClaims', ['claimData', 'loading', 'validators']),
    ...mapState('backend', ['encodedContextPath']),
  },

  mounted() {
    this.fetchClaimData(this.claimId);
    this.setCSRF(this.csrf);
    this.validators.push(this.$validator);
  },

  methods: {
    addNewClaimLine() {
      this.expandedClaims.push(`line${this.claimData.claimLines.length}`);

      let highestId = 1;

      if (this.claimData.claimLines && this.claimData.claimLines.length) {
        highestId = parseInt(this.claimData.claimLines[this.claimData.claimLines.length - 1].id);
      }

      this.claimData.claimLines.push({
        id: `${highestId + 1}`,
        productType: 'Bikes',
        qty: null,
        selectedDamagedOccurred: 'enumDamageOccurredAfterPurchase',
        selectedRequestedAction: 'enumRequestedActionReplace',
        warrantyDamageImage: {name: null, url: null, encodedImage: null},
        warrantyPurchaseProofImage: {name: null, url: null, encodedImage: null},
        warrantySerialNumberImage: {name: null, url: null, encodedImage: null},
        warrantyClaimIssueUfoCode: '',
        warrantyLineNumber: '',
        warrantyOptionalAttachments: [],
        serialNumberImage: {name: null, url: null, encodedImage: null},
        damageMethod: {code: null},
        purchasedDate: '',
        purchaseDateDisplay: '',
        serialNumber: '',
        status: '',
        size: '',
        sku: '',
        modelYear: '',
        modelName: '',
      });

      this.autoSaveClaim({csrf: this.csrf});
    },

    claimLineExpansionChange(payload) {
      this.expandedClaims = payload;
    },

    deleteClaimLine() {
      this.isDeleteClaimLineDialogVisible = false;

      if (this.claimData.id) {
        // If we have a claim id then this claim has been autosaved and we can BE delete the line
        axios
          .delete(`/claims/delete/${this.claimData.id}/${this.claimData.id}_${this.claimLineToDelete}`)
          .finally(() => this.fetchClaimData(this.claimData.id));
      } else {
        // Else we can just locally delete it as this claim doesn't exist in the BE yet
        remove(this.claimData.claimLines, (claimLine) => claimLine.id === this.claimLineToDelete);

        // Re-assign ids based on order
        this.claimData.claimLines.forEach((claimLine, index) => {
          claimLine.id = `${index + 1}`;
        });

        this.$notify({
          type: 'success',
          message: this.bp('deleteClaimLineThankYou'),
        });
      }
    },

    openclaimLineDeleteDialog(claimLineToDelete) {
      this.isDeleteClaimLineDialogVisible = true;
      this.claimLineToDelete = claimLineToDelete;
    },

    deleteClaim() {
      this.isDeleteClaimDialogVisible = false;

      axios.delete(`/claims/delete/${this.claimData.id}`).then((response) => {
        const message = get(response, 'data.meta.feedback.message.basePropertyKey', null);

        if (message === 'warrantyClaims.B2B.claimDeleted') {
          setTimeout(() => {
            window.location = `${this.encodedContextPath}/warranty-claim/all-warranty-claims/`;
          }, 3000);
        }
      });
    },

    validateForm(validate) {
      let validatorPromises = [];
      this.validators.forEach((validator) => {
        validatorPromises.push(
          new Promise(function(resolve) {
            resolve(validator.validateAll());
          })
        );
      });

      // Use Promise.all to run all validator promises concurrently and catching the final result in one function in order
      // to save or ignore
      return Promise.all(validatorPromises).then((values) => {
        if (every(values, (value) => value)) {
          return this.saveProcessedClaim({validate});
        } else {
          window.vm.$scrollTo('body');
        }
      });
    },

    submitClaim() {
      this.claimSubmitted = true;

      this.validateForm(true)
        .then((submitResponse) => {
          if (submitResponse && submitResponse.data && submitResponse.data.meta.feedback.type === 'success') {
            const feedbackMsg = this.bp('claimSubmit', submitResponse.data.data.id);
            this.setFeedbackMsg(feedbackMsg);
            window.location = `${ACC.config.encodedContextPath}/warranty-claim/all-warranty-claims/`;
          } else {
            this.claimSubmitted = false;
          }
        })
        .finally(() => {
          this.setUseToastInterceptor(true);
        });
    },

    ...mapActions('warrantyClaims', ['fetchClaimData', 'autoSaveClaim', 'saveProcessedClaim', 'saveClaim']),
    ...mapActions('storage', ['setFeedbackMsg']),
    ...mapMutations('global', ['setUseToastInterceptor']),
    ...mapMutations('warrantyClaims', ['setCSRF']),
  },
};
</script>
